import { ref } from "vue";
import manualService from "@/services/manualService";

export function useManuais() {
  const manual = ref({
    id: null,
    productId: null,
    title: "",
    description: "",
    countryVisibility: [],
    topics: [],
    footerTitle: "",
    footerContent: "",
    videoTutorialUrl: "",
    disabled: false,
  });

  const getInitialManualState = () => ({
    id: null,
    productId: null,
    title: "",
    description: "",
    countryVisibility: [],
    topics: [],
    footerTitle: "",
    footerContent: "",
    videoTutorialUrl: "",
    disabled: false,
  });

  const topic = ref({
    tempId: null,
    manualId: null,
    title: "",
    content: "",
    mobilePictureUrl: "",
    desktopPictureUrl: "",
    mobilePictureFile: null,
    desktopPictureFile: null,
    mobilePicturePreview: null,
    desktopPicturePreview: null,
    order: 1,
    disabled: false,
    removed: false,
  });

  const carregando = ref(false);
  const saving = ref(false);
  const erro = ref(null);

  const carregarManual = async (idProduto) => {
    carregando.value = true;
    erro.value = null;

    try {
      const response = await manualService.buscarPorIdProduto(idProduto);

      if (response?.data && response.data.length > 0) {
        const manualAPI = response.data[0];
        manual.value = {
          id: manualAPI.id,
          productId: manualAPI.productId,
          title: manualAPI.title || "",
          description: manualAPI.description || "",
          countryVisibility: manualAPI.countryVisibility || [],
          topics: (manualAPI.topics || []).sort((a, b) => a.order - b.order),
          footerTitle: manualAPI.footerTitle || "",
          footerContent: manualAPI.footerContent || "",
          videoTutorialUrl: manualAPI.videoTutorialUrl || "",
          disabled: Boolean(manualAPI.disabled),
        };

        manual.value.videoTutorialUrls = manual.value.videoTutorialUrl
          ? manual.value.videoTutorialUrl.split(";").map((u) => u.trim())
          : [];
      } else {
        return null;
      }
      return response.data;
    } catch (err) {
      erro.value = err.message || "Erro ao carregar manual";
      console.error("❌ Erro ao carregar manual:", err);
      return null;
    } finally {
      carregando.value = false;
    }
  };

  const salvarManual = async () => {
    try {
      saving.value = true;
      erro.value = null;
      
      // Serializa as URLs de vídeo
      let videoTutorialUrl = "";
      if (Array.isArray(manual.value.videoTutorialUrls)) {
        const urlsValidas = manual.value.videoTutorialUrls
          .map((url) => url.trim())
          .filter((url) => url.length > 0);
        videoTutorialUrl = urlsValidas.join(";");
      }

      const formData = new FormData();

      // Campos básicos
      formData.append("productId", String(manual.value.productId));
      formData.append("title", manual.value.title);
      formData.append("description", manual.value.description);
      formData.append("footerTitle", manual.value.footerTitle);
      formData.append("footerContent", manual.value.footerContent);
      formData.append("videoTutorialUrl", videoTutorialUrl);
      formData.append("disabled", String(manual.value.disabled));

      if (manual.value.id != null) {
        formData.append("id", String(manual.value.id));
      }

      formData.append(
        "countryVisibility",
        JSON.stringify(manual.value.countryVisibility)
      );

      // Clona os tópicos e remove arquivos para enviar apenas os dados JSON
      const topicsParaEnvio = manual.value.topics.map((topico) => {
        return {
          id: topico.id || undefined,
          title: topico.title,
          content: topico.content,
          order: topico.order,
          disabled: topico.disabled,
          mobilePictureUrl: topico.mobilePictureUrl || "",
          desktopPictureUrl: topico.desktopPictureUrl || "",
        };
      });
      
      formData.append("topics", JSON.stringify(topicsParaEnvio));


      // Uploads de imagens
      manual.value.topics.forEach((topico, index) => {
        const idKey = topico.id ?? `new_${index}`;

        if (topico.mobilePictureFile) {
          formData.append(
            `topicsMobilePictures_${idKey}`,
            topico.mobilePictureFile
          );
        }

        if (topico.desktopPictureFile) {
          formData.append(
            `topicsDesktopPictures_${idKey}`,
            topico.desktopPictureFile
          );
        }
      });
      
      // Chamada da API
      let response;
      if (manual.value.id) {
        response = await manualService.atualizarManual(
          manual.value.id,
          formData
        );
      } else {
        response = await manualService.criarManual(formData);
      }

      manual.value = response.data;
      return true;
    } catch (err) {
      erro.value = err.message || "Erro ao salvar manual";
      console.error("❌ Erro ao salvar manual:", err);
      return false;
    } finally {
      carregando.value = false;
      saving.value = false;
    }
  };

  const processarImagem = (arquivo, tipo = "mobile", topico) => {
    if (!arquivo) return;

    if (!arquivo.type.startsWith("image/")) {
      erro.value = "Por favor, selecione apenas arquivos de imagem";
      return;
    }

    if (arquivo.size > 5 * 1024 * 1024) {
      erro.value = "A imagem deve ter no máximo 5MB";
      return;
    }

    // Salvar arquivo e gerar preview
    if (tipo === "mobile") {
      topico.mobilePictureFile = arquivo;
      const reader = new FileReader();
      reader.onload = (e) => {
        topico.mobilePicturePreview = e.target?.result;
      };
      reader.readAsDataURL(arquivo);
    } else {
      topico.desktopPictureFile = arquivo;
      const reader = new FileReader();
      reader.onload = (e) => {
        topico.desktopPicturePreview = e.target?.result;
      };
      reader.readAsDataURL(arquivo);
    }

    erro.value = null;
  };

  /**
   * Limpa a imagem do tópico
   * @param {Object} topico - tópico alvo
   * @param {string} tipo - "mobile" ou "desktop"
   */
  const limparImagem = (topico, tipo = "mobile") => {
    if (tipo === "mobile") {
      topico.mobilePictureFile = null;
      topico.mobilePictureUrl = "";
      topico.mobilePicturePreview = null;
    } else {
      topico.desktopPictureFile = null;
      topico.desktopPictureUrl = "";
      topico.desktopPicturePreview = null;
    }
  };

  const resetManual = () => {
    manual.value = getInitialManualState();
    // Garante que a lista de URLs de vídeo também seja limpa
    manual.value.videoTutorialUrls = [];
  };

  return {
    manual,
    topic,
    saving,
    carregando,
    erro,
    resetManual,
    carregarManual,
    salvarManual,
    processarImagem,
    limparImagem,
  };
}

export default useManuais;
